import { Flex, FlexProps, PopoverProps } from "@chakra-ui/react";
import { Release } from "../../types";
import { ReleasePriceLabel } from "./ReleasePriceLabel";
import { ReleaseActionsButtons } from "../releases/components/ReleaseActionsButtons";
import { ReaderNavigationContext } from "../../screens/reader/types";
import { SaleBadge } from "./components/SaleBadge";
import { FunnelConfigTypes, FunnelContextArea, EventContextArea } from "../funnel/types";

export interface ReleasePricingActionsProps extends FlexProps {
  release: Release;
  setFunnelConfig: (config: FunnelConfigTypes) => void;
  contextArea: FunnelContextArea;
  eventContextArea?: EventContextArea;

  primaryButtonMaxWidth?: string | Record<string, string>;
  navigationContext?: ReaderNavigationContext;
  onlyShowALCActions?: boolean;
  hideFreeWithGold?: boolean;
  hidePreviewAction?: boolean;
  displayAsARow?: boolean;
  showSaleBadge?: boolean;
  labelFontSize?: string | Record<string, string> | number;
  buttonSize?: "sm" | "md" | "lg";
  contextualButtonDirection?: PopoverProps["placement"];
  escapeContainerBounds?: boolean;
  wrapButtons?: boolean;
}

export function ReleasePricingActions({
  release,
  setFunnelConfig,
  contextArea,
  eventContextArea,
  hideFreeWithGold = false,
  primaryButtonMaxWidth,
  navigationContext,
  onlyShowALCActions = false,
  hidePreviewAction = false,
  displayAsARow = false,
  showSaleBadge = false,
  labelFontSize,
  buttonSize,
  contextualButtonDirection = "bottom",
  escapeContainerBounds = false,
  wrapButtons = false,
  ...flexProps
}: ReleasePricingActionsProps) {
  const maxWidth = calculateMaxWidth(primaryButtonMaxWidth);

  return (
    <Flex
      flexDirection={displayAsARow ? "row" : "column"}
      gap={displayAsARow ? "16px" : "8px"}
      width={"100%"}
      maxWidth={maxWidth}
      alignItems={displayAsARow ? "center" : "flex-start"}
      {...flexProps}
    >
      {showSaleBadge && <SaleBadge release={release} showInCorner={false} />}
      <ReleasePriceLabel
        release={release}
        hideFreeWithGold={hideFreeWithGold}
        fontSize={labelFontSize}
      />
      <ReleaseActionsButtons
        release={release}
        contextArea={contextArea}
        eventContextArea={eventContextArea}
        setFunnelConfig={setFunnelConfig}
        primaryButtonMaxWidth={primaryButtonMaxWidth}
        navigationContext={navigationContext}
        onlyShowALCActions={onlyShowALCActions}
        hidePreviewButton={hidePreviewAction}
        buttonSize={buttonSize}
        wrapButtons={wrapButtons}
        contextualButtonDirection={contextualButtonDirection}
        escapeContainerBounds={escapeContainerBounds}
      />
    </Flex>
  );
}

// Calculate maxWidth based on primaryButtonMaxWidth + 94px
const calculateMaxWidth = (width?: string | Record<string, string>) => {
  if (!width) return undefined;

  if (typeof width === "string") {
    // Handle string values like "250px", "100%", etc.
    if (width.endsWith("px")) {
      const numericValue = parseInt(width.replace("px", ""));
      return `${numericValue + 94}px`;
    }
    // For other string values like "100%", "auto", etc., we can't add 94px
    return `calc(${width} + 94px)`;
  } else {
    // Handle responsive object like { base: "200px", md: "300px" }
    const responsiveMaxWidth: Record<string, string> = {};
    Object.entries(width).forEach(([breakpoint, value]) => {
      if (value.endsWith("px")) {
        const numericValue = parseInt(value.replace("px", ""));
        responsiveMaxWidth[breakpoint] = `${numericValue + 94}px`;
      } else {
        responsiveMaxWidth[breakpoint] = `calc(${value} + 94px)`;
      }
    });
    return responsiveMaxWidth;
  }
};
